<template>
  <div class="dropdown-menu" :strategy="props.strategy">
    <div
      :class="['dropdown-trigger', { active: isOpen, disabled: props.disabled }]"
      @click="handleTriggerClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      ref="triggerRef"
    >
      <slot name="trigger" :active="isOpen" :disabled="props.disabled" />
    </div>

    <component :is="props.strategy === 'absolute' ? 'div' : Teleport" :to="popupContainer">
      <div
        v-if="isOpen"
        ref="contentRef"
        class="dropdown-content"
        :class="{ 'dropdown-content--positioned': isPositioned }"
        :style="contentStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <slot name="content" />
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import type { Subscription } from 'rxjs';
import type { CSSProperties } from 'vue';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  Teleport,
  watch,
  type Ref,
} from 'vue';
import { MODAL_LAYER_CONTEXT_KEY } from '../../constants/layer-context';
import { SubjectKeys, subjectManager } from '../../libs/subjects';
import { getWindow } from '../../utils/ssr';

interface DropdownMenuProps {
  trigger?: 'click' | 'hover';
  position?:
    | 'bottomCenter'
    | 'bottomLeft'
    | 'bottomRight'
    | 'right'
    | 'topCenter'
    | 'topLeft'
    | 'topRight';
  contentStyle?: CSSProperties;
  open?: boolean;
  manual?: boolean;
  gap?: number | [number, number];
  useMinWidth?: boolean;
  /** 为 true 时，下拉框宽度与 trigger 对齐（与 trigger 等宽） */
  contentMatchTriggerWidth?: boolean;
  // 点击外部关闭时的白名单，支持元素、ref 或选择器字符串
  clickOutsideIgnore?: (HTMLElement | Ref<HTMLElement | undefined> | string)[];
  // 是否禁用，禁用时 dropdown 不生效但 trigger 正常展示
  disabled?: boolean;
  /** 浮层挂载容器，默认 body。在 Modal 内使用时由 Modal 提供，无需手动传入 */
  getPopupContainer?: () => string | HTMLElement;
  /** 自定义 z-index，用于覆盖默认层级。在 Modal 内时自动使用 --x-layer-dropdown-over-modal */
  zIndex?: number | string;
  /** 定位策略，默认为 fixed。设置为 absolute 时，下拉框将跟随触发器自然滚动，不再移动到 body */
  strategy?: 'fixed' | 'absolute';
}

const props = withDefaults(defineProps<DropdownMenuProps>(), {
  trigger: 'click',
  contentStyle: () => ({}),
  position: 'bottomLeft',
  useMinWidth: true,
  contentMatchTriggerWidth: false,
  disabled: false,
  strategy: 'fixed',
});

const modalLayerContext = inject<{ zIndex: number } | undefined>(
  MODAL_LAYER_CONTEXT_KEY,
  undefined,
);

const popupContainer = computed(() => {
  if (props.getPopupContainer) {
    return props.getPopupContainer();
  }
  return 'body';
});

const resolvedZIndex = computed(() => {
  if (props.zIndex !== undefined) {
    return props.zIndex;
  }
  return modalLayerContext ? 'var(--x-layer-dropdown-over-modal)' : 'var(--x-layer-dropdown)';
});

const gap = computed(() =>
  Array.isArray(props.gap)
    ? { x: props.gap[0], y: props.gap[1] }
    : { x: props.gap ?? 4, y: props.gap ?? 4 },
);

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'clickOutside'): void;
}>();

const innerOpen = ref(false);
const isOpen = computed<boolean>({
  get: () => (props.manual ? !!props.open : innerOpen.value),
  set: value => {
    if (props.manual) {
      emit('update:open', value);
    } else {
      innerOpen.value = value;
    }
  },
});
const triggerRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();
const closeTimer = ref<number>();
const subscriptions = ref<Subscription[]>([]);
const isPositioned = ref(false);

// 检查是否应该阻止自动行为
const shouldBlockAutoBehavior = () => props.disabled || props.manual;

// 清理关闭定时器
const clearCloseTimer = () => {
  if (closeTimer.value) {
    clearTimeout(closeTimer.value);
    closeTimer.value = undefined;
  }
};

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const close = () => {
  clearCloseTimer();
  // 在 manual 模式下立即关闭，非 manual 模式下延迟关闭（用于 hover 模式避免快速移出时立即关闭）
  if (props.manual) {
    isOpen.value = false;
  } else {
    const win = getWindow();
    if (win) {
      closeTimer.value = win.setTimeout(() => {
        isOpen.value = false;
      }, 100);
    }
  }
};

const open = () => {
  if (props.disabled) return;
  clearCloseTimer();
  isOpen.value = true;
};

const contentStyle = ref<CSSProperties>({});

const handleTriggerClick = (event: Event) => {
  if (shouldBlockAutoBehavior() || props.trigger !== 'click') return;
  event.stopPropagation();
  toggle();
};

const handleMouseEnter = () => {
  if (shouldBlockAutoBehavior() || props.trigger !== 'hover') return;
  open();
};

const handleMouseLeave = () => {
  if (props.manual || props.trigger !== 'hover') return;
  close();
};

// 获取内容尺寸
const getContentDimensions = (trigger: HTMLElement, content?: HTMLElement) => {
  if (!content) {
    return {
      width: trigger.clientWidth,
      height: 200,
    };
  }

  return {
    width: content.offsetWidth || trigger.clientWidth,
    height: content.offsetHeight || 200,
  };
};

// 计算可用空间
const calculateAvailableSpace = (triggerRect: DOMRect, viewportHeight: number) => {
  return {
    spaceBelow: viewportHeight - triggerRect.bottom - gap.value.y,
    spaceAbove: triggerRect.top - gap.value.y,
  };
};

// 计算右侧位置
const calculateRightPosition = (
  triggerRect: DOMRect,
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number,
) => {
  // 纵向以触发器顶部对齐并做边界收敛
  let top = triggerRect.top;
  top = Math.max(gap.value.y, Math.min(top, viewportHeight - contentHeight - gap.value.y));

  // 水平优先放在右侧，不够则回退到左侧
  let left = triggerRect.right + gap.value.x;
  let transformOrigin: 'left' | 'right' = 'left';

  if (left + contentWidth + gap.value.x > viewportWidth) {
    left = triggerRect.left - contentWidth - gap.value.x;
    transformOrigin = 'right';
  }
  return { top, left, transformOrigin };
};

// 计算垂直位置
const calculateVerticalPosition = (
  triggerRect: DOMRect,
  contentHeight: number,
  spaceBelow: number,
  spaceAbove: number,
) => {
  let top: number;
  let transformOrigin: string;
  if (props.position.startsWith('top')) {
    top = triggerRect.top - contentHeight - gap.value.y;
    transformOrigin = 'bottom';
  } else {
    if (spaceBelow >= contentHeight || spaceBelow >= spaceAbove) {
      top = triggerRect.bottom + gap.value.y;
      transformOrigin = 'top';
    } else {
      top = triggerRect.top - contentHeight - gap.value.y;
      transformOrigin = 'bottom';
    }
  }

  return { top, transformOrigin };
};

// 计算水平位置
const calculateHorizontalPosition = (
  triggerRect: DOMRect,
  contentWidth: number,
  position: string,
) => {
  if (position.endsWith('Right')) {
    return triggerRect.right - contentWidth;
  }
  if (position.endsWith('Center')) {
    return triggerRect.left + (triggerRect.width - contentWidth) / 2;
  }
  // bottomLeft, topLeft 或默认
  return triggerRect.left;
};

// 确保位置不超出屏幕边界
const constrainToViewport = (
  top: number,
  left: number,
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number,
) => {
  return {
    top: Math.max(gap.value.y, Math.min(top, viewportHeight - contentHeight - gap.value.y)),
    left: Math.max(gap.value.x, Math.min(left, viewportWidth - contentWidth - gap.value.x)),
  };
};

// 创建内容样式对象
const createContentStyle = (
  top: number,
  left: number,
  triggerWidth: number,
  transformOrigin: string,
  additionalStyle?: CSSProperties,
): CSSProperties => {
  return {
    position: props.strategy || 'fixed',
    ...(props.useMinWidth && { minWidth: `${triggerWidth}px` }),
    ...(props.contentMatchTriggerWidth && {
      width: `${triggerWidth}px`,
      minWidth: `${triggerWidth}px`,
      maxWidth: `${triggerWidth}px`,
      boxSizing: 'border-box',
    }),
    top: `${top}px`,
    left: `${left}px`,
    zIndex: resolvedZIndex.value,
    display: 'block',
    transformOrigin,
    ...additionalStyle,
  };
};

const calculatePosition = () => {
  const trigger = triggerRef.value;
  const content = contentRef.value;

  if (!trigger) return;
  const win = getWindow();
  if (!win) return; // SSR 安全检查

  const triggerRect = trigger.getBoundingClientRect();
  const viewportHeight = win.innerHeight;
  const viewportWidth = win.innerWidth;

  // 获取内容尺寸
  const { width: contentWidth, height: contentHeight } = getContentDimensions(trigger, content);

  // 当位置为 right 时，使用右侧定位逻辑
  if (props.position === 'right') {
    const { top, left, transformOrigin } = calculateRightPosition(
      triggerRect,
      contentWidth,
      contentHeight,
      viewportWidth,
      viewportHeight,
    );

    contentStyle.value = createContentStyle(
      top,
      left,
      trigger.clientWidth,
      transformOrigin,
      props.contentStyle,
    );

    nextTick(() => {
      isPositioned.value = true;
    });

    return;
  }

  // 计算可用空间
  const { spaceBelow, spaceAbove } = calculateAvailableSpace(triggerRect, viewportHeight);

  // 计算垂直位置
  const { top: verticalTop, transformOrigin } = calculateVerticalPosition(
    triggerRect,
    contentHeight,
    spaceBelow,
    spaceAbove,
  );

  // 计算水平位置
  const horizontalLeft = calculateHorizontalPosition(triggerRect, contentWidth, props.position);

  // 确保位置不超出屏幕边界
  let finalTop = verticalTop;
  let finalLeft = horizontalLeft;

  if (props.strategy !== 'absolute') {
    const constrained = constrainToViewport(
      verticalTop,
      horizontalLeft,
      contentWidth,
      contentHeight,
      viewportWidth,
      viewportHeight,
    );
    finalTop = constrained.top;
    finalLeft = constrained.left;
  } else {
    // 如果是 absolute，位置应该是相对于 .dropdown-menu (relative 父级) 的
    // 由于 triggerRef.value 就在 .dropdown-menu 内部且占满位置
    // 我们需要减去当前 triggerRect 的起始位置来获得相对量
    finalTop = finalTop - triggerRect.top;
    finalLeft = finalLeft - triggerRect.left;
  }

  // 设置内容样式
  contentStyle.value = createContentStyle(
    finalTop,
    finalLeft,
    trigger.clientWidth,
    transformOrigin,
    props.contentStyle,
  );

  nextTick(() => {
    isPositioned.value = true;
  });
};

// 监听下拉菜单状态变化
watch(
  () => isOpen.value,
  async open => {
    if (open) {
      // 如果被禁用，直接关闭
      if (props.disabled) {
        isOpen.value = false;
        return;
      }
      // 重置位置状态，确保在计算完成前内容不可见
      isPositioned.value = false;
      await nextTick();
      // 使用 requestAnimationFrame 确保在下一帧计算位置，避免抖动
      requestAnimationFrame(() => {
        calculatePosition();
      });
    } else {
      isPositioned.value = false;
    }
  },
);

// 监听 disabled 状态变化，如果变为 disabled 且当前打开，则关闭
// 在 manual 模式下，由外部控制，不自动关闭
watch(
  () => props.disabled,
  disabled => {
    if (disabled && isOpen.value && !props.manual) {
      isOpen.value = false;
    }
  },
);

const handlePageResize = () => {
  if (isOpen.value) {
    requestAnimationFrame(() => {
      calculatePosition();
    });
  }
};

const handleGlobalScroll = (event: Event) => {
  if (!isOpen.value || props.manual) return;

  // 如果是 absolute 策略，天然随父级滚动，不需要处理关闭逻辑
  if (props.strategy === 'absolute') return;

  const target = event.target as Node | null;
  const contentEl = contentRef.value;
  const triggerEl = triggerRef.value;

  if (
    target &&
    (contentEl?.contains(target) ||
      triggerEl?.contains(target) ||
      (triggerEl && target instanceof Node && target.contains(triggerEl)))
  ) {
    return;
  }

  close();
};

// 创建 ignore 列表的辅助函数（返回函数以支持响应式更新）
const createIgnoreListGetter = (defaultRef: Ref<HTMLElement | undefined>) => {
  return () => {
    const list: (HTMLElement | Ref<HTMLElement | undefined> | string)[] = [defaultRef];
    if (props.clickOutsideIgnore) {
      list.push(...props.clickOutsideIgnore);
    }
    return list;
  };
};

// 处理点击外部事件的统一逻辑
const handleClickOutside = () => {
  if (!isOpen.value) return;
  emit('clickOutside');
  close();
};

// 使用 vueuse 监听点击外部关闭（分别监听内容和触发器，互相忽略）
// ignore 使用函数形式以支持响应式更新
onClickOutside(contentRef, handleClickOutside, { ignore: createIgnoreListGetter(triggerRef) });
onClickOutside(triggerRef, handleClickOutside, { ignore: createIgnoreListGetter(contentRef) });

onMounted(() => {
  const resizeSub = subjectManager.getSubject(SubjectKeys.PageResize)?.subscribe(handlePageResize);
  const scrollSub = subjectManager
    .getSubject(SubjectKeys.GlobalScroll)
    ?.subscribe(handleGlobalScroll);

  if (resizeSub) subscriptions.value.push(resizeSub);
  if (scrollSub) subscriptions.value.push(scrollSub);
});

onBeforeUnmount(() => {
  subscriptions.value.forEach(sub => sub.unsubscribe());
  subscriptions.value = [];
  clearCloseTimer();
});

defineExpose({
  close,
  open,
  toggle,
});
</script>

<style scoped lang="scss">
.dropdown-menu {
  position: relative;
  display: inline-block;
  width: fit-content;
  /* 防止异常的撑开 */
  line-height: 0;
}

.dropdown-trigger {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.dropdown-content {
  display: block;
  width: fit-content;
  visibility: hidden; /* 默认隐藏，避免位置计算前的闪烁 */
  z-index: 100;
  line-height: initial;
}

/* 只有在 fixed 模式下才应用默认的阴影和圆角，absolute 模式完全交给插槽内容控制 */
.dropdown-menu:not([strategy='absolute']) .dropdown-content {
  box-shadow: var(--x-shadow);
  border-radius: var(--x-radius-medium);
}

.dropdown-content--positioned {
  visibility: visible; /* 位置计算完成后显示 */
}
</style>
